import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSupabaseMutation } from '@/shared/hooks/useSupabaseMutation';
import { useSupabaseSubscription } from '@/hooks/use-supabase-subscription';
import { UsuariosPendentesSupabaseService } from '@/services/supabase/usuariosPendentesService';
import { ConsultoresSupabaseService } from '@/services/supabase/consultoresService';
import { Database } from '@/integrations/supabase/types';

type PendingUser = Database['public']['Tables']['profiles']['Row'] & {
  email?: string;
  status?: string;
  selectedTenant?: string | null;
  selectedRole?: string | null;
  selectedSupervisor?: number | null;
};


export function useUsuariosPendentes() {
  // Buscar usuários pendentes - usa useQuery diretamente pois a página já está protegida por SUPER_ADMIN
  const {
    data: pendingUsers = [],
    isLoading: isLoadingUsers,
    error: usersError,
    refetch: refetchUsers
  } = useQuery({
    queryKey: ['supabase', 'usuarios-pendentes'],
    queryFn: () => UsuariosPendentesSupabaseService.listarUsuariosPendentes(),
    enabled: true,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  // Buscar tenants ativos
  const {
    data: tenants = [],
    isLoading: isLoadingTenants,
    error: tenantsError,
    refetch: refetchTenants
  } = useQuery({
    queryKey: ['supabase', 'tenants-ativos'],
    queryFn: () => UsuariosPendentesSupabaseService.listarTenants(),
    enabled: true,
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  // Buscar supervisores (será carregado dinamicamente baseado no tenant selecionado)
  const [supervisores, setSupervisores] = useState<any[]>([]);
  const [isLoadingSupervisores, setIsLoadingSupervisores] = useState(false);

  // Mutação para aprovar usuário
  const aprovarUsuarioMutation = useSupabaseMutation(
    async ({ userId, tenantId, role, supervisorId }: { userId: string; tenantId: string; role: string; supervisorId?: number }) => {
      const result = await UsuariosPendentesSupabaseService.aprovarUsuario(userId, tenantId, role, supervisorId);
      return { data: result, error: null };
    },
    {
      invalidateQueries: ['usuarios-pendentes', 'tenants-ativos'],
      successMessage: 'Usuário aprovado com sucesso!',
      errorMessage: 'Erro ao aprovar usuário'
    }
  );

  // Mutação para rejeitar usuário
  const rejeitarUsuarioMutation = useSupabaseMutation(
    async ({ userId }: { userId: string }) => {
      const result = await UsuariosPendentesSupabaseService.rejeitarUsuario(userId);
      return { data: result, error: null };
    },
    {
      invalidateQueries: ['usuarios-pendentes'],
      successMessage: 'Usuário rejeitado com sucesso!',
      errorMessage: 'Erro ao rejeitar usuário'
    }
  );

  // Mutação para atualizar tenant
  const atualizarTenantMutation = useSupabaseMutation(
    async ({ userId, tenantId }: { userId: string; tenantId: string }) => {
      const result = await UsuariosPendentesSupabaseService.atualizarTenantUsuario(userId, tenantId);
      return { data: result, error: null };
    },
    {
      invalidateQueries: ['usuarios-pendentes'],
      successMessage: 'Tenant atualizado com sucesso!',
      errorMessage: 'Erro ao atualizar tenant'
    }
  );

  // Mutação para atualizar role
  const atualizarRoleMutation = useSupabaseMutation(
    async ({ userId, role }: { userId: string; role: string }) => {
      const result = await UsuariosPendentesSupabaseService.atualizarRoleUsuario(userId, role);
      return { data: result, error: null };
    },
    {
      invalidateQueries: ['usuarios-pendentes'],
      successMessage: 'Role atualizado com sucesso!',
      errorMessage: 'Erro ao atualizar role'
    }
  );

  // Subscrição para mudanças na tabela profiles
  useSupabaseSubscription({
    channel: 'profiles_changes',
    event: '*',
    table: 'profiles',
    callback: (payload) => {
      console.log('Mudança detectada na tabela profiles:', payload);
      refetchUsers();
    }
  });

  // Subscrição para mudanças na tabela tenants
  useSupabaseSubscription({
    channel: 'tenants_changes',
    event: '*',
    table: 'tenants',
    callback: (payload) => {
      console.log('Mudança detectada na tabela tenants:', payload);
      refetchTenants();
    }
  });

  // Função para carregar supervisores baseado no tenant
  const carregarSupervisores = async (tenantId: string) => {
    if (!tenantId) {
      setSupervisores([]);
      return;
    }

    setIsLoadingSupervisores(true);
    try {
      const supervisoresData = await ConsultoresSupabaseService.listarSupervisoresPorTenant(Number(tenantId));
      setSupervisores(supervisoresData);
    } catch (error) {
      console.error('Erro ao carregar supervisores:', error);
      setSupervisores([]);
    } finally {
      setIsLoadingSupervisores(false);
    }
  };

  // Processar usuários pendentes para adicionar campos de seleção
  // tenant_id já vem do auto-cadastro (empresa escolhida pelo usuário)
  const processedPendingUsers: PendingUser[] = Array.isArray(pendingUsers) ? pendingUsers.map((user: any) => ({
    ...user,
    selectedTenant: user.tenant_id != null ? String(user.tenant_id) : null,
    selectedRole: user.role && user.role !== 'USER' ? user.role : null,
    selectedSupervisor: null,
    status: user.status || 'PENDING'
  })) : [];

  // Filtrar apenas usuários realmente pendentes (status PENDING)
  const usuariosPendentes = processedPendingUsers.filter(user => 
    user.status === 'PENDING'
  );

  return {
    // Dados
    usuariosPendentes,
    tenants,
    supervisores,
    
    // Estados de loading
    isLoading: isLoadingUsers || isLoadingTenants,
    isLoadingUsers,
    isLoadingTenants,
    isLoadingSupervisores,
    
    // Erros
    usersError,
    tenantsError,
    
    // Mutações
    aprovarUsuario: aprovarUsuarioMutation.mutate,
    rejeitarUsuario: rejeitarUsuarioMutation.mutate,
    atualizarTenant: atualizarTenantMutation.mutate,
    atualizarRole: atualizarRoleMutation.mutate,
    
    // Estados das mutações
    isAprovando: aprovarUsuarioMutation.isPending,
    isRejeitando: rejeitarUsuarioMutation.isPending,
    isAtualizandoTenant: atualizarTenantMutation.isPending,
    isAtualizandoRole: atualizarRoleMutation.isPending,
    
    // Funções auxiliares
    carregarSupervisores,
    
    // Refetch
    refetchUsers,
    refetchTenants,
    refetchAll: () => {
      refetchUsers();
      refetchTenants();
    }
  };
}
